/**
 * Selectable laboratory result card used beside the trend graph.
 */

import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { LabResultItem } from '../../../types';
import { statusModifier } from './labTrendUtils';

/**
 * Properties for an evaluated or unavailable laboratory marker card.
 */
interface LabResultCardProps {
  item: LabResultItem;
  selected: boolean;
  onSelect?: (item: LabResultItem) => void;
}

/**
 * Render an evaluated lab value or a collapsed unavailable marker.
 *
 * @param props - Result item, selection state, and selection callback.
 * @returns The selectable laboratory card.
 */
export function LabResultCard({
  item,
  selected,
  onSelect,
}: LabResultCardProps) {
  const isEvaluated = item.evaluated !== false && item.value !== null;
  const TrendIcon =
    item.trend === 'Increasing'
      ? TrendingUp
      : item.trend === 'Decreasing'
        ? TrendingDown
        : Minus;
  const modifier = statusModifier(item.status);

  return (
    <button
      type="button"
      className={`lab-result-card lab-result-card--${modifier} ${
        selected ? 'lab-result-card-selected' : ''
      }`}
      onClick={isEvaluated && onSelect ? () => onSelect(item) : undefined}
      disabled={!isEvaluated}
      aria-pressed={selected}
      aria-label={
        isEvaluated
          ? `Show ${item.label} trend`
          : `${item.label}: Not evaluated`
      }
    >
      <span className="lab-card-header">
        <span className="lab-card-name">{item.label}</span>
        {isEvaluated && item.trend && (
          <span className="lab-card-trend-icon" title={item.trend}>
            <TrendIcon size={16} aria-hidden="true" />
          </span>
        )}
      </span>
      {isEvaluated && (
        <>
          <span className="lab-card-value">{item.value}</span>
          <span className="lab-card-unit">
            {item.unit || 'No unit reported'}
          </span>
        </>
      )}
      <span className={`lab-card-status-badge lab-status--${modifier}`}>
        {item.status}
      </span>
    </button>
  );
}

export default LabResultCard;
